/**
 * Thin wrapper over the three `connection-transfer:*` channels.
 *
 * The passphrase travels renderer → main and nowhere else. It is never put in
 * a store, never persisted, never logged, and never becomes an analytics
 * property — the only thing that comes back is counts, endpoint metadata and
 * error strings that are asserted (in `connectionTransferIpc.test.ts`) to
 * contain neither the passphrase nor any secret.
 *
 * The `localStorage` test overrides mirror `settingsTransferApi` — e2e cannot
 * click a native dialog, so in test mode the renderer supplies the path and
 * main skips the dialog. Both are gated on `testMode.isTestMode()`.
 */
import type {
  ConnectionExportResult,
  ConnectionTransferApplyResult,
  ConnectionTransferPreviewResult,
} from '../../../types/connectionTransfer';

const TEST_EXPORT_PATH_KEY = 'sql-studio-test-connections-export-file';
const TEST_IMPORT_PATH_KEY = 'sql-studio-test-connections-import-file';

/**
 * Shown wherever an export is offered. The baseline half of the file is plain
 * JSON by design — that is what makes it readable and importable — so the one
 * thing a user must know before mailing it is what "no passwords" still
 * exposes.
 */
export const CONNECTIONS_EXPORT_CAVEAT =
  'The file lists server names, ports, database names, usernames and any SSH ' +
  'bastion — in plain text, so you can read it before you share it. Passwords ' +
  'and SSH secrets are never written unless you add a passphrase below.';

function testOverridePath(key: string): string | undefined {
  if (!window.electronAPI?.testMode?.isTestMode?.()) return undefined;
  if (typeof localStorage === 'undefined') return undefined;
  return localStorage.getItem(key)?.trim() || undefined;
}

const unavailable = { success: false as const, error: 'Not available here.' };

export async function exportConnectionsToFile(args: {
  connectionIds: string[];
  passphrase?: string;
  suggestedFileName: string;
}): Promise<ConnectionExportResult> {
  const api = window.electronAPI?.connectionTransfer;
  if (!api?.exportConnections) return unavailable;
  const filePath = testOverridePath(TEST_EXPORT_PATH_KEY);
  return api.exportConnections({
    connectionIds: args.connectionIds,
    ...(args.passphrase ? { passphrase: args.passphrase } : {}),
    suggestedFileName: args.suggestedFileName,
    ...(filePath ? { filePath } : {}),
  });
}

export async function previewConnectionsFile(): Promise<ConnectionTransferPreviewResult> {
  const api = window.electronAPI?.connectionTransfer;
  if (!api?.previewImport) return unavailable;
  const filePath = testOverridePath(TEST_IMPORT_PATH_KEY);
  return api.previewImport(filePath ? { filePath } : {});
}

export async function applyConnectionsImport(args: {
  filePath: string;
  exportIds: string[];
  passphrase?: string;
}): Promise<ConnectionTransferApplyResult> {
  const api = window.electronAPI?.connectionTransfer;
  if (!api?.applyImport) return { success: false, error: 'Not available here.' };
  return api.applyImport({
    filePath: args.filePath,
    exportIds: args.exportIds,
    ...(args.passphrase ? { passphrase: args.passphrase } : {}),
  });
}

export function connectionsFileName(): string {
  return `jam-sql-studio-connections-${new Date().toISOString().slice(0, 10)}.json`;
}
